/**
 * auth/AuthContext.jsx — Authentication state management (Phase 2).
 *
 * Provides login/logout, token persistence, and user state.
 * Wraps the entire app to enforce auth on protected routes.
 *
 * Phase 2 additions:
 *   - Refresh token storage in sessionStorage
 *   - Automatic session restore via refresh on mount
 *   - Backend logout call before clearing local state
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  auth as authApi,
  setToken,
  clearToken,
  setRefreshToken,
  clearRefreshToken,
} from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'rently_token';
const REFRESH_KEY = 'rently_refresh_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const savedToken = sessionStorage.getItem(TOKEN_KEY);
      const savedRefresh = sessionStorage.getItem(REFRESH_KEY);

      if (!savedToken) {
        setLoading(false);
        return;
      }

      setToken(savedToken);
      if (savedRefresh) setRefreshToken(savedRefresh);

      try {
        // Try with saved access token (auto-refresh on 401 is handled by api.js)
        const u = await authApi.me();
        setUser(u);
      } catch {
        // Both access and refresh failed — clear and show login
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_KEY);
        clearToken();
        clearRefreshToken();
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await authApi.login(email, password);
      setToken(result.access_token);
      sessionStorage.setItem(TOKEN_KEY, result.access_token);

      // Phase 2: store refresh token
      if (result.refresh_token) {
        setRefreshToken(result.refresh_token);
        sessionStorage.setItem(REFRESH_KEY, result.refresh_token);
      }

      setUser({
        sub: email,
        role: result.role,
        agency_id: result.agency_id,
      });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    // Phase 2: call backend logout endpoint for all-session revocation
    try {
      await authApi.logout();
    } catch {
      // Continue logout even if backend call fails (token may already be expired)
    }
    clearToken();
    clearRefreshToken();
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export default AuthContext;
